// Exchange (booking) request + transition payload schemas.
// The state machine lives in state-machines/exchange.ts; the worker validates
// the request body with these zod schemas, then constructs the typed
// ExchangeEvent for the machine.
//
// Wave B refactor: `art_form` validates structurally via `slugSchema`. The
// existence check (slug present + enabled in art_forms) happens in the worker
// route handler against the live KV vocab snapshot.
import { z } from 'zod';

import {
  currencyCodeSchema,
  idSchema,
  isoDateSchema,
  moneyMinorSchema,
  richTextSchema,
  vocabSlugSchema,
} from './common.js';

const optionalLetter = richTextSchema(8000);

export const draftExchangeInputSchema = z.object({
  venue_id: idSchema,
  start_date: isoDateSchema,
  end_date: isoDateSchema,
  party_size: z.number().int().positive().max(50),
  art_form: vocabSlugSchema,
  opening_letter: optionalLetter.optional(),
  structured_metadata: z
    .object({
      gig: z.string().max(2000).optional(),
      ask: z.string().max(2000).optional(),
      intro: z.string().max(2000).optional(),
    })
    .optional(),
  stipend_offered: z.boolean().optional().default(false),
  stipend_amount_minor: moneyMinorSchema.optional(),
  stipend_currency: currencyCodeSchema.optional(),
});
export type DraftExchangeInput = z.infer<typeof draftExchangeInputSchema>;

export const submitExchangeInputSchema = z
  .object({
    exchange_id: idSchema,
    opening_letter: richTextSchema(8000).refine((s) => s.trim().length >= 80, {
      message: 'Letter should be at least 80 characters — give the host something to read.',
    }),
    party_size: z.number().int().positive().max(50),
    start_date: isoDateSchema,
    end_date: isoDateSchema,
    art_form: vocabSlugSchema,
    stipend_offered: z.boolean().optional().default(false),
    stipend_amount_minor: moneyMinorSchema.optional(),
    stipend_currency: currencyCodeSchema.optional(),
    structured_metadata: z
      .object({
        gig: z.string().max(2000).optional(),
        ask: z.string().max(2000).optional(),
        intro: z.string().max(2000).optional(),
      })
      .optional(),
  })
  .refine((v) => v.start_date <= v.end_date, {
    message: 'start_date must be ≤ end_date',
    path: ['end_date'],
  });
export type SubmitExchangeInput = z.infer<typeof submitExchangeInputSchema>;

export const confirmExchangeInputSchema = z.object({
  exchange_id: idSchema,
});
export type ConfirmExchangeInput = z.infer<typeof confirmExchangeInputSchema>;

export const declineExchangeInputSchema = z.object({
  exchange_id: idSchema,
  reason: z.string().max(2000).optional(),
  share_with_artist: z.boolean().default(false),
});
export type DeclineExchangeInput = z.infer<typeof declineExchangeInputSchema>;

export const cancelExchangeInputSchema = z.object({
  exchange_id: idSchema,
  reason: z.string().min(1).max(2000),
});
export type CancelExchangeInput = z.infer<typeof cancelExchangeInputSchema>;

export const markCompleteInputSchema = z.object({
  exchange_id: idSchema,
});
export type MarkCompleteInput = z.infer<typeof markCompleteInputSchema>;

export const listExchangesQuerySchema = z.object({
  state: z
    .enum([
      'drafted',
      'submitted',
      'confirmed',
      'completed',
      'cancelled',
      'declined',
      'disputed',
      'closed',
    ])
    .optional(),
  cursor: z.string().min(1).max(256).optional(),
  limit: z.coerce.number().int().positive().max(100).default(25),
});
export type ListExchangesQuery = z.infer<typeof listExchangesQuerySchema>;
